// Como cada ingrediente pode ser lançado na compra e como a quantidade vai para a API.
//
// O estoque fica sempre na unidade do ingrediente (`unit`: un | kg). As medidas abaixo são
// as formas de contar a mesma compra; todas aparecem juntas no formulário (v1.5.723):
//  - un + units_per_package → pacotes e unidades. A API recebe PACOTES (o backend multiplica).
//  - kg + unit_weight       → quilos e unidades (1 un = X kg). A API recebe kg.
//  - un + unit_weight       → unidades e quilos. A API recebe un.
import { numero } from "./numeros";

const SIGLAS = { un: "un", kg: "kg", g: "g", l: "L", lt: "L", ml: "ml" };

export const sigla = (unit) => SIGLAS[String(unit || "un").toLowerCase()] || unit || "un";
const ehPeso = (unit) => ["kg", "l", "lt"].includes(String(unit || "").toLowerCase());

// "36 un", "4,500 kg", "1,5 L"
export function quantidade(v, unit) {
  const n = Number(v) || 0;
  if (ehPeso(unit)) return `${numero(n, 3)} ${sigla(unit)}`;
  return `${numero(n, 2, { minimo: Number.isInteger(n) ? 0 : 2 })} ${sigla(unit)}`;
}

const plural = (n, um, varios) => (Math.abs(n - 1) < 1e-9 ? um : varios);

// `fator` = quanto vale 1 desta medida na unidade do estoque.
export function medidasDoIngrediente(ing) {
  if (!ing) return [];
  const unit = String(ing.unit || "un").toLowerCase();
  const pacote = Number(ing.units_per_package) || 0;
  const peso = Number(ing.unit_weight) || 0;
  const unidade = { id: "unidade", campo: "Unidades", um: "unidade", varios: "unidades", curto: "un", fator: 1 };
  const kg = (fator) => ({ id: "kg", campo: "Quilos", um: "kg", varios: "kg", curto: "kg", fator, peso: true });
  if (unit === "un" && pacote > 0) {
    return [{ id: "pacote", campo: "Pacotes", um: "pacote", varios: "pacotes", curto: "pct", fator: pacote, detalhe: `1 pacote = ${numero(pacote, 3, { minimo: 0 })} un` }, unidade];
  }
  if (ehPeso(unit) && peso > 0) {
    const medida = { id: unit, campo: unit === "kg" ? "Quilos" : "Litros", um: sigla(unit), varios: sigla(unit), curto: sigla(unit), fator: 1, peso: true };
    return [medida, { ...unidade, fator: peso, detalhe: `1 un = ${numero(peso, 3)} ${sigla(unit)}` }];
  }
  if (unit === "un" && peso > 0) {
    return [unidade, { ...kg(1 / peso), detalhe: `1 un = ${numero(peso, 3)} kg` }];
  }
  if (ehPeso(unit)) return [{ id: unit, campo: unit === "kg" ? "Quilos" : "Litros", um: sigla(unit), varios: sigla(unit), curto: sigla(unit), fator: 1, peso: true }];
  return [unidade];
}

// Compatibilidade com quem só precisa da lista de medidas.
export const formasDeLancar = medidasDoIngrediente;

// "6 pacotes", "1 unidade", "4,500 kg"
export function naForma(qtd, forma) {
  if (!forma) return "";
  if (forma.peso) return `${numero(qtd, 3)} ${forma.um}`;
  return `${numero(qtd, 2, { minimo: Number.isInteger(Number(qtd)) ? 0 : 2 })} ${plural(qtd, forma.um, forma.varios)}`;
}

// A quantidade escrita nas duas medidas: "1 pacote" + "6 un". Pacote quebrado
// (8 latas = 1,33 pacote) não ajuda ninguém: nesse caso mostra só as unidades.
const inteiro = (v) => Math.abs(v - Math.round(v)) < 1e-6;

export function emDuasMedidas(ing, qtdEstoque) {
  const medidas = medidasDoIngrediente(ing);
  if (!medidas.length) return { principal: quantidade(qtdEstoque, ing?.unit), secundaria: null };
  const mostra = medidas.filter((m) => m.peso || inteiro(qtdEstoque / m.fator));
  const lista = mostra.length ? mostra : [medidas[medidas.length - 1]];
  return {
    principal: naForma(qtdEstoque / lista[0].fator, lista[0]),
    secundaria: lista[1] ? naForma(qtdEstoque / lista[1].fator, lista[1]) : null,
  };
}

export function paraApi(ing, qtdEstoque) {
  const pacote = Number(ing?.units_per_package) || 0;
  if (String(ing?.unit || "").toLowerCase() === "un" && pacote > 0) return qtdEstoque / pacote;
  return qtdEstoque;
}
